import React, { useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, Send, X } from "lucide-react";
import { createPublicSupportTicket } from "../../services/supportService";
import "./PublicSupportModal.css";

const categories = [
    { value: "general", label: "General" },
    { value: "website", label: "Website / Preview" },
    { value: "deployment", label: "Generation / Publish Issue" },
    { value: "billing", label: "Billing" },
    { value: "account", label: "Login / Account" },
    { value: "bug", label: "Bug Report" }
];

export default function PublicSupportModal({
    open,
    onClose,
    previewSessionId,
    businessName,
    industry
}) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        category: "general",
        priority: "medium",
        subject: "",
        message: ""
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(null);

    const contextText = useMemo(() => {
        const items = [];

        if (businessName) items.push(`Business: ${businessName}`);
        if (industry) items.push(`Industry: ${industry}`);
        if (previewSessionId) items.push(`Preview Session: ${previewSessionId}`);

        return items.join(" • ");
    }, [businessName, industry, previewSessionId]);

    if (!open) return null;

    const update = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess(null);

        if (!form.email.trim() || !form.subject.trim() || !form.message.trim()) {
            setError("Email, subject, and message are required.");
            return;
        }

        try {
            setLoading(true);

            const browserInfo = {
                userAgent: navigator.userAgent,
                language: navigator.language,
                screen: `${window.screen.width}x${window.screen.height}`
            };

            const response = await createPublicSupportTicket({
                name: form.name.trim(),
                email: form.email.trim(),
                category: form.category,
                priority: form.priority,
                subject: form.subject.trim(),
                message: `${form.message.trim()}

Context:
${contextText || "No preview context available"}

Browser:
${JSON.stringify(browserInfo, null, 2)}`,
                previewSessionId: previewSessionId || null,
                businessName: businessName || "",
                industry: industry || "",
                pageUrl: window.location.href
            });

            setSuccess(response);
            setForm({
                name: "",
                email: "",
                category: "general",
                priority: "medium",
                subject: "",
                message: ""
            });
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Unable to create support ticket."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="public-support-backdrop">
            <div className="public-support-modal">
                <div className="public-support-header">
                    <div>
                        <span>Support</span>
                        <h2>Need help?</h2>
                        <p>Tell us what happened and our team will help you.</p>
                    </div>

                    <button type="button" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {contextText && (
                    <div className="public-support-context">
                        {contextText}
                    </div>
                )}

                {error && (
                    <div className="public-support-alert error">
                        <AlertCircle size={16} />
                        {error}
                    </div>
                )}

                {success && (
                    <div className="public-support-alert success">
                        <CheckCircle2 size={16} />
                        Ticket created: <strong>{success.ticketNumber}</strong>
                    </div>
                )}

                <form onSubmit={submit} className="public-support-form">
                    <div className="public-support-grid">
                        <label>
                            Name
                            <input
                                value={form.name}
                                onChange={(e) => update("name", e.target.value)}
                                placeholder="Your name"
                            />
                        </label>

                        <label>
                            Email *
                            <input
                                type="email"
                                value={form.email}
                                onChange={(e) => update("email", e.target.value)}
                                placeholder="you@example.com"
                            />
                        </label>
                    </div>

                    <div className="public-support-grid">
                        <label>
                            Issue Type
                            <select
                                value={form.category}
                                onChange={(e) => update("category", e.target.value)}
                            >
                                {categories.map((x) => (
                                    <option key={x.value} value={x.value}>
                                        {x.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label>
                            Priority
                            <select
                                value={form.priority}
                                onChange={(e) => update("priority", e.target.value)}
                            >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </label>
                    </div>

                    <label>
                        Subject *
                        <input
                            value={form.subject}
                            onChange={(e) => update("subject", e.target.value)}
                            placeholder="Example: My website preview is stuck"
                        />
                    </label>

                    <label>
                        Message *
                        <textarea
                            rows={6}
                            value={form.message}
                            onChange={(e) => update("message", e.target.value)}
                            placeholder="Describe the issue. What were you trying to do?"
                        />
                    </label>

                    <div className="public-support-actions">
                        <button type="button" onClick={onClose} className="support-cancel-btn">
                            Cancel
                        </button>

                        <button type="submit" disabled={loading} className="support-submit-btn">
                            {loading ? <Loader2 className="spin" size={16} /> : <Send size={16} />}
                            Submit Ticket
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}